/**
 * Constants for the terminal-flow (finance-flow) payment dialog.
 *
 * These mirror the socket contract used by the finance-flow terminal: the web
 * client emits `web_connection` events and listens for `payment_update` /
 * terminal-connection events on the {@link SOCKET_EVENTS.FINANCE_FLOW} channel.
 */

/**
 * High-level flow a terminal dialog can drive. Each one runs on its own socket
 * channel — see {@link TERMINAL_FLOW_SOCKET_EVENT}.
 */
export const TERMINAL_FLOW_TYPE = {
  FINANCE_FLOW: 'finance_flow',
  TERMINAL_PAYMENT: 'payment_process',
  SUBSCRIPTION: 'subscription'
} as const;

/** `type` field on a finance-flow socket message (both inbound and outbound). */
export const TERMINAL_EVENT_TYPE = {
  /** Inbound: a payment state change (status carried in the payload). */
  PAYMENT_UPDATE: 'payment_update',
  /** Outbound: the web client announcing/closing its terminal connection. */
  WEB_CONNECTION: 'web_connection',
  // TODO: confirm these four against the actual terminal-payment socket contract —
  // placeholders following the PAYMENT_UPDATE/WEB_CONNECTION naming convention.
  /** Inbound: the S700 reader's connection status changed (e.g. FAILED). */
  S700_CONNECTION: 's700_connection',
  /** Inbound: the terminal collected a setup intent to attach as a payment source. */
  ADD_PAYMENT_SOURCE: 'add_payment_source',
  /** Inbound: the terminal is unreachable for this transaction. */
  TERMINAL_UNAVAILABLE: 'terminal_unavailable',
  /** Outbound: the web client kicking off a terminal-payment transaction. */
  INITIATE_CONNECTION: 'initiate_connection'
} as const;

/** `connection_status` sent on a WEB_CONNECTION event. */
export const TERMINAL_CONNECTION_STATUS = {
  PENDING: 'PENDING',
  CONNECTED: 'CONNECTED',
  FAILED: 'FAILED',
  CANCELLED: 'CANCELLED',
  CLOSED: 'CLOSED'
} as const;

/** `status` carried on an inbound PAYMENT_UPDATE payload. */
export const PAYMENT_STATUS = {
  SUCCESS: 'SUCCESS',
  INPROGRESS: 'INPROGRESS',
  FAILED: 'FAILED'
} as const;

/**
 * Outcome code emitted to the caller (via `paymentUpdate`) when the dialog
 * closes. The caller uses it to decide what to show after the terminal flow.
 */
export const TRANSACTION_STATUS = {
  SUCCESS: 'success',
  /**
   * The terminal finished by sending the customer a payment link instead of
   * taking payment on the reader — nothing was charged, so this is reported
   * separately from SUCCESS.
   */
  PROPOSAL_SENT: 'proposal_sent',
  /** Dialog dismissed / cancelled from the web side. */
  CANCELLED_FROM_WEB: 'cancelled_from_web',
  /** Physical terminal / POS was not (or is no longer) connected. */
  TERMINAL_NOT_CONNECTED: 'terminal_not_connected',
  /** The underlying socket dropped mid-flow. */
  SOCKET_DISCONNECT: 'socket_disconnect'
} as const;

/** socket.io event names this feature emits on / listens to. */
export const SOCKET_EVENTS = {
    CUSTOMER_DETAILS_SUBMITTED: 'DL_UPLOADED',
    TERMINAL_PAYMENT: 'terminal_socket',
    FINANCE_FLOW: 'lendee_terminal_socket',
    SUBSCRIPTION_FLOW: 'subscription_terminal_socket',
    TERMINAL_CONNECTION: 'device_event',
    LIVE_VIEW: 'live_view_socket'
}

/**
 * The socket.io channel each flow's transaction runs on.
 *
 * All three channels carry the same envelope (`{ type, data }` with
 * terminal-connection events and `payment_update`s), but the terminal keeps a
 * separate channel per flow, so the dialog resolves its channel from the flow
 * it was opened for instead of hard-coding one. Unknown flows fall back to the
 * finance-flow channel at the call site.
 */
export const TERMINAL_FLOW_SOCKET_EVENT: Readonly<Record<string, string>> = {
  [TERMINAL_FLOW_TYPE.FINANCE_FLOW]: SOCKET_EVENTS.FINANCE_FLOW,
  [TERMINAL_FLOW_TYPE.SUBSCRIPTION]: SOCKET_EVENTS.SUBSCRIPTION_FLOW,
  [TERMINAL_FLOW_TYPE.TERMINAL_PAYMENT]: SOCKET_EVENTS.TERMINAL_PAYMENT
};

/**
 * Internal lifecycle state of the terminal-flow dialog (single source of truth,
 * replacing the old transactionInProgress / transactionCompleted / serverErrorOccured
 * booleans). `WAITING` and `IN_PROGRESS` are the only non-terminal states.
 * Distinct from {@link TRANSACTION_STATUS}, which is the outcome reported to the caller.
 */
export const TERMINAL_FLOW_STATE = {
  /** Dialog opened, connecting / awaiting the terminal. */
  WAITING: 'waiting',
  /** Terminal is actively processing the payment. */
  IN_PROGRESS: 'in_progress',
  /** Payment succeeded. */
  SUCCESS: 'success',
  /** Payment failed on the terminal. */
  FAILED: 'failed',
  /** Terminal / POS connection was closed or errored. */
  TERMINAL_NOT_CONNECTED: 'terminal_not_connected',
  /** Cancelled from the web side (dialog dismissed by the user). */
  CANCELLED: 'cancelled',
  /** Underlying socket dropped mid-flow. */
  SERVER_ERROR: 'server_error',
  /** Completed via an external signal (transactionCompleted$). */
  COMPLETED: 'completed'
} as const;

/**
 * MatDialog id of the terminal-flow dialog. Set when it is opened so other
 * screens (e.g. the checkout panel watching `device_event`) can find and close
 * it when the reader it is driving drops off.
 */
export const TERMINAL_FLOW_DIALOG_ID = 'terminal-flow-dailog';

/**
 * MatDialog id of the server-issue dialog. Only one may be open at a time —
 * several readers can drop at once, and each drop would otherwise stack
 * another copy of the same message.
 */
export const TERMINAL_SERVER_ISSUE_DIALOG_ID = 'server-issue-dialog';

/**
 * MatDialog id of the terminal-in-use confirmation. Only one may be open at a
 * time — the send button can be pressed repeatedly while the dialog is up.
 */
export const TERMINAL_IN_USE_DIALOG_ID = 'terminal-in-use-dialog';

/**
 * Copy for the terminal-in-use confirmation. A reader reporting `IN_USE` is
 * still reachable, so the send isn't blocked outright — the merchant is warned
 * that it will take over whatever is on the reader and asked to confirm.
 */
export const TERMINAL_IN_USE = {
  TITLE: 'Terminal In Use',
  LEAD: 'The selected terminal is currently being used by someone else.',
  BODY:
    'Sending these plans now will replace whatever is on the terminal screen. Continue only if ' +
    'you are sure the terminal is free to use.',
  CONFIRM: 'Send Anyway',
  CANCEL: 'Cancel'
} as const;

/**
 * Copy for the server-issue dialog. Static by design: the dialog says one
 * thing — the connection to the terminal was lost mid-transaction — so the
 * wording lives here rather than being passed in per caller.
 */
export const TERMINAL_SERVER_ISSUE = {
  TITLE: 'Server Issue Detected',
  LEAD: 'We’re currently experiencing a server issue.',
  BODY:
    'Your transaction is still in progress on the terminal. Please complete or cancel it ' +
    'directly on the terminal before selecting another payment option.',
  NOTE: 'Once done, refresh the page to view the updated transaction status.',
  ACTION: 'Ok'
} as const;

/** User-facing copy for terminal-flow outcomes. */
export const TERMINAL_FLOW_MESSAGES = {
  CONNECTION_CLOSED:
    'The terminal connection was closed. Please log in to the terminal, ' +
    'select another payment option, or restart the terminal payment.',
  PAYMENT_SUCCESSFUL: 'Payment successful',
  PROPOSAL_SENT: 'Payment link sent to the customer.',
  TRANSACTION_FAILED: 'Transaction failed.',
  CANCELLED_BY_USER: 'Transaction cancelled by user.',
  /**
   * Shown when the user tries to reload/close the tab mid-transaction. Most
   * browsers ignore the custom copy and render their own generic prompt, but a
   * non-empty value is still required to trigger it at all.
   */
  RELOAD_WARNING: 'A terminal transaction is still in progress. Leaving this page will cancel it.',
  DEFAULT_DEVICE_NAME: 'Front Desk Terminal'
} as const;

export const LIVE_VIEW_FLOW_TYPE = {
    FINANCING_TERMINAL: 'transaction',
    SUBSCRIPTION_TERMINAL: 'subscription_terminal',
    PAYMENT_PROCESS_TERMINAL: 'payment_process',
} as const;

/** `type` field on an inbound message on the live-view channel. */
export const LIVE_VIEW_EVENT_TYPE = {
  /** The terminal moved to a new screen; payload is a LiveViewFrame. */
  FRAME: 'live_view_frame'
} as const;

/**
 * `screen_name` values the dialog reacts to (rather than just renders).
 *
 * The terminal suffixes screen names per flow, so each logical screen appears
 * once per flow it can occur in.
 */
export const LIVE_VIEW_SCREEN_NAME = {
  /** Terminal reached the end of its flow — the dialog can stop mirroring. */
  FLOW_COMPLETED: 'flow_completed',
  FLOW_COMPLETED_SUBSCRIPTION: 'flow_completed_subscription',
  FLOW_COMPLETED_PP: 'flow_completed_payment_process',
  /** Customer was sent a payment link instead of paying on the reader. */
  PAYMENT_LINK_SENT: 'payment_link_success',
  PAYMENT_LINK_SENT_SUBSCRIPTION: 'payment_link_success_subscription',
  PAYMENT_LINK_SENT_PP: 'payment_link_success_payment_process',
  /** Reader shows the payment went through (e.g. charged to a card on file). */
  TRANSACTION_SUCCESS_SUBSCRIPTION: 'transaction_success_subscription'
} as const;

/**
 * How long the dialog keeps mirroring the terminal after a successful payment
 * before closing on its own.
 *
 * A success no longer closes the dialog immediately — the merchant should see
 * the rest of the terminal's screens (feedback, review, …) — so the outcome is
 * held until a `flow_completed*` frame arrives. The timer is re-armed on every
 * frame, and only fires if the terminal goes quiet without ever completing,
 * which would otherwise strand the dialog on the last screen forever.
 */
export const LIVE_VIEW_SUCCESS_FLUSH_MS = 30000;